import { z } from 'zod';

const portNumberSchema = z.int().min(1).max(65535);

export const createPortsSchema = ({
  maxCustomPorts,
}: {
  maxCustomPorts: number;
}) =>
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('top'),
      count: z.int().min(1).max(65535),
    }),
    z.object({ kind: z.literal('full') }),
    z.object({
      kind: z.literal('custom'),
      ports: z.array(portNumberSchema).min(1).max(maxCustomPorts),
    }),
  ]);

export const PortsSchema = createPortsSchema({ maxCustomPorts: 65535 });
export type Ports = z.infer<typeof PortsSchema>;

export const WordlistSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('off') }),
  z.object({ kind: z.literal('small') }),
  z.object({ kind: z.literal('large') }),
  z.object({ kind: z.literal('custom'), wordlistId: z.string().min(1) }),
]);
export type WordlistSelection = z.infer<typeof WordlistSelectionSchema>;

export const VersionIntensitySchema = z.enum(['light', 'standard', 'all']);
export type VersionIntensity = z.infer<typeof VersionIntensitySchema>;

export const ScanTypeSchema = z.enum(['syn', 'connect']);
export type ScanType = z.infer<typeof ScanTypeSchema>;

export const BaseStageOptionsSchema = z.object({
  timeoutMs: z.int().positive(),
});
export type BaseStageOptions = z.infer<typeof BaseStageOptionsSchema>;

export const NetworkStageOptionsSchema = BaseStageOptionsSchema.extend({
  rate: z.int().positive(),
  concurrency: z.int().positive(),
});
export type NetworkStageOptions = z.infer<typeof NetworkStageOptionsSchema>;

export const NaabuStageOptionsSchema = NetworkStageOptionsSchema.extend({
  ports: PortsSchema,
  scanType: ScanTypeSchema,
});
export type NaabuStageOptions = z.infer<typeof NaabuStageOptionsSchema>;

export const DnsBruteforceStageOptionsSchema = NetworkStageOptionsSchema.extend(
  { wordlistSize: WordlistSelectionSchema },
);
export type DnsBruteforceStageOptions = z.infer<
  typeof DnsBruteforceStageOptionsSchema
>;

export const NmapStageOptionsSchema = NetworkStageOptionsSchema.extend({
  versionIntensity: VersionIntensitySchema,
  scanType: ScanTypeSchema,
});
export type NmapStageOptions = z.infer<typeof NmapStageOptionsSchema>;

export const UdpPortSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('off') }),
  z.object({
    kind: z.literal('top'),
    count: z.int().min(1).max(65535),
  }),
  z.object({ kind: z.literal('full') }),
  z.object({
    kind: z.literal('custom'),
    ports: z.array(portNumberSchema).min(1).max(1024),
  }),
]);
export type UdpPortSelection = z.infer<typeof UdpPortSelectionSchema>;

export const UdpNmapPortSpecSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('top'),
    count: z.int().min(1).max(65535),
  }),
  z.object({ kind: z.literal('full') }),
  z.object({
    kind: z.literal('explicit'),
    ports: z.array(portNumberSchema).min(1).max(1024),
  }),
]);
export type UdpNmapPortSpec = z.infer<typeof UdpNmapPortSpecSchema>;

export const UdpNmapStageOptionsSchema = NetworkStageOptionsSchema.extend({
  versionIntensity: VersionIntensitySchema,
  udpPortSelection: UdpPortSelectionSchema,
});
export type UdpNmapStageOptions = z.infer<typeof UdpNmapStageOptionsSchema>;

export type StageOptionsMap = {
  'passive-dns-lookup': BaseStageOptions;
  'permutation-discovery': BaseStageOptions;
  'dns-resolution': NetworkStageOptions;
  'reverse-dns': NetworkStageOptions;
  'dns-bruteforce': DnsBruteforceStageOptions;
  'alive-host-detection': NaabuStageOptions;
  'port-scanning': NaabuStageOptions;
  'udp-service-detection': UdpNmapStageOptions;
  'service-detection': NmapStageOptions;
  'passive-intelligence': BaseStageOptions;
  'cdn-check': BaseStageOptions;
};

export type ScannerStage = keyof StageOptionsMap;

export const STAGE_OPTIONS_SCHEMA: {
  [TStage in ScannerStage]: z.ZodType<StageOptionsMap[TStage]>;
} = {
  'passive-dns-lookup': BaseStageOptionsSchema,
  'permutation-discovery': BaseStageOptionsSchema,
  'dns-resolution': NetworkStageOptionsSchema,
  'reverse-dns': NetworkStageOptionsSchema,
  'dns-bruteforce': DnsBruteforceStageOptionsSchema,
  'alive-host-detection': NaabuStageOptionsSchema,
  'port-scanning': NaabuStageOptionsSchema,
  'udp-service-detection': UdpNmapStageOptionsSchema,
  'service-detection': NmapStageOptionsSchema,
  'passive-intelligence': BaseStageOptionsSchema,
  'cdn-check': BaseStageOptionsSchema,
};

export const ScanConfigOverridesSchema = z
  .strictObject({
    'passive-dns-lookup': BaseStageOptionsSchema.partial(),
    'permutation-discovery': BaseStageOptionsSchema.partial(),
    'dns-resolution': NetworkStageOptionsSchema.partial(),
    'reverse-dns': NetworkStageOptionsSchema.partial(),
    'dns-bruteforce': DnsBruteforceStageOptionsSchema.partial(),
    'alive-host-detection': NaabuStageOptionsSchema.partial(),
    'port-scanning': NaabuStageOptionsSchema.partial(),
    'udp-service-detection': UdpNmapStageOptionsSchema.partial(),
    'service-detection': NmapStageOptionsSchema.partial(),
    'passive-intelligence': BaseStageOptionsSchema.partial(),
    'cdn-check': BaseStageOptionsSchema.partial(),
  })
  .partial()
  .describe('Per-stage tool config overrides keyed by scanner stage');
export type ScanConfigOverrides = z.infer<typeof ScanConfigOverridesSchema>;
